import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { navigateToEnterpriseDemo } from "@/utils/navigation";

const SolutionPricingSection = () => {
  return (
    <section className="relative py-20 bg-background overflow-hidden">
      {/* Subtle background gradient animation */}
      <motion.div
        className="absolute inset-0 opacity-30"
        animate={{
          background: [
            'radial-gradient(ellipse at 20% 50%, rgba(16, 185, 129, 0.1) 0%, transparent 50%)',
            'radial-gradient(ellipse at 80% 50%, rgba(16, 185, 129, 0.1) 0%, transparent 50%)',
            'radial-gradient(ellipse at 20% 50%, rgba(16, 185, 129, 0.1) 0%, transparent 50%)',
          ]
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <Badge variant="outline" className="mb-4">Modular Flexibility Solution</Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Design Your <span className="bg-gradient-primary bg-clip-text text-transparent">Fusion Solution</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            No two marketing teams work the same way. That's why Fusion is modular. Start with the Platform. Add AI Lab when you're ready for custom workflows. Bring in Creation Desk when you need expert firepower. Your team, your way.
          </p>
        </motion.div>

        {/* Horizontal Table Layout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-12 overflow-hidden rounded-2xl border border-border shadow-lg max-w-4xl mx-auto"
        >
          {/* Header Row */}
          <div className="hidden md:grid md:grid-cols-[200px_1fr_180px] bg-emerald-900 text-white">
            <div className="p-5 font-semibold text-sm uppercase tracking-wider">Solution</div>
            <div className="p-5 font-semibold text-sm uppercase tracking-wider border-l border-emerald-800 text-center">Choose From</div>
            <div className="p-5 font-semibold text-sm uppercase tracking-wider border-l border-emerald-800 text-center">Start up time</div>
          </div>

          {/* Row 1 - Platform */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-[200px_1fr_180px] border-t border-border first:border-t-0 transition-all duration-200"
            whileHover={{ backgroundColor: 'rgba(16, 185, 129, 0.05)' }}
          >
            <div className="bg-emerald-100 dark:bg-emerald-950/40 p-5 flex items-center">
              <span className="text-foreground font-bold text-lg uppercase tracking-wide">Platform</span>
            </div>
            <div className="bg-emerald-100 dark:bg-emerald-950/40 p-5 border-l border-border flex flex-col items-center justify-center">
              <p className="text-xs font-semibold text-muted-foreground mb-3 md:hidden">Choose From</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <div className="flex-1 min-w-[200px] text-center p-4 rounded-xl bg-white dark:bg-background border border-emerald-200 dark:border-emerald-800">
                  <p className="text-sm font-medium text-foreground">User based pricing</p>
                  <p className="text-xs text-muted-foreground mt-1 whitespace-nowrap">Number of user seats</p>
                </div>
                <div className="flex-1 min-w-[220px] text-center p-4 rounded-xl bg-white dark:bg-background border border-emerald-200 dark:border-emerald-800">
                  <p className="text-sm font-medium text-foreground">Usage based pricing</p>
                  <p className="text-xs text-muted-foreground mt-1 whitespace-nowrap">Number of credits per month</p>
                </div>
              </div>
            </div>
            <div className="bg-emerald-100 dark:bg-emerald-950/40 p-5 border-l border-border flex items-center justify-center">
              <p className="text-xs font-semibold text-muted-foreground mb-1 md:hidden mr-2">Start up time:</p>
              <motion.span
                className="text-sm font-medium text-emerald-600 dark:text-emerald-400"
                animate={{ opacity: [1, 0.7, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                Start right away
              </motion.span>
            </div>
          </motion.div>

          {/* Row 2 - AI Lab */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-[200px_1fr_180px] border-t border-border transition-all duration-200"
            whileHover={{ backgroundColor: 'rgba(16, 185, 129, 0.03)' }}
          >
            <div className="bg-emerald-50 dark:bg-emerald-950/20 p-5 flex items-center">
              <span className="text-foreground font-bold text-lg uppercase tracking-wide">AI Lab</span>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-950/20 p-5 border-l border-border flex flex-col items-center justify-center">
              <p className="text-xs font-semibold text-muted-foreground mb-3 md:hidden">Choose From</p>
              <div className="flex flex-wrap gap-2 justify-center">
                <span className="px-4 py-2 text-xs font-medium rounded-full bg-white dark:bg-background border border-emerald-200 dark:border-emerald-800 text-foreground">
                  Additional knowledge layer
                </span>
                <span className="px-4 py-2 text-xs font-medium rounded-full bg-white dark:bg-background border border-emerald-200 dark:border-emerald-800 text-foreground">
                  Use case or functionality
                </span>
                <span className="px-4 py-2 text-xs font-medium rounded-full bg-white dark:bg-background border border-emerald-200 dark:border-emerald-800 text-foreground">
                  Integrations
                </span>
                <span className="px-4 py-2 text-xs font-medium rounded-full bg-white dark:bg-background border border-emerald-200 dark:border-emerald-800 text-foreground">
                  More
                </span>
              </div>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-950/20 p-5 border-l border-border flex items-center justify-center">
              <p className="text-xs font-semibold text-muted-foreground mb-1 md:hidden mr-2">Start up time:</p>
              <span className="text-sm font-medium text-foreground">1–3 weeks</span>
            </div>
          </motion.div>

          {/* Row 3 - Creation Desk */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-[200px_1fr_180px] border-t border-border transition-all duration-200"
            whileHover={{ backgroundColor: 'rgba(16, 185, 129, 0.05)' }}
          >
            <div className="bg-emerald-100 dark:bg-emerald-950/40 p-5 flex items-center">
              <span className="text-foreground font-bold text-lg uppercase tracking-wide">Creation Desk</span>
            </div>
            <div className="bg-emerald-100 dark:bg-emerald-950/40 p-5 border-l border-border flex flex-col items-center justify-center">
              <p className="text-xs font-semibold text-muted-foreground mb-3 md:hidden">Choose From</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <div className="flex-1 min-w-[140px] text-center p-4 rounded-xl bg-white dark:bg-background border border-emerald-200 dark:border-emerald-800 flex items-center justify-center">
                  <p className="text-sm font-medium text-foreground">Ad hoc</p>
                </div>
                <div className="flex-1 min-w-[140px] text-center p-4 rounded-xl bg-white dark:bg-background border border-emerald-200 dark:border-emerald-800 flex items-center justify-center">
                  <p className="text-sm font-medium text-foreground">Annual commitment</p>
                </div>
              </div>
            </div>
            <div className="bg-emerald-100 dark:bg-emerald-950/40 p-5 border-l border-border flex items-center justify-center">
              <p className="text-xs font-semibold text-muted-foreground mb-1 md:hidden mr-2">Start up time:</p>
              <span className="text-sm font-medium text-foreground">2–3 weeks</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Global CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center"
        >
          <Button variant="hero" size="lg" className="px-8 py-6 text-base" onClick={() => navigateToEnterpriseDemo()}>
            Build your Perfect Fusion
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default SolutionPricingSection;